import { FlexColumn, useStyles } from '@gorila-shared-ui/components';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { OnChangeParams } from 'baseui/select';
import { ParagraphXSmall } from 'baseui/typography';
import { useEffect } from 'react';
import { useCommandFieldConfig } from '../../../hooks/useCommandFieldConfig';
import { CommandField } from '../../../types/availableCommand';
import { EnhancedLabeledSelect } from '../../ui/EnhancedLabeledSelect';

type Props = {
  field: CommandField;
  onChange: (value: string | number) => void;
};

export function CommandFields({ field, onChange }: Readonly<Props>) {
  const { theme } = useStyles();
  const fieldConfig = useCommandFieldConfig(field.fieldType);

  const fieldLabel = `${field.name} ${field.required ? '*' : ''}`;

  useEffect(() => {
    // Initialize field value with defaultValue if it's not already set
    if (field.value === undefined && field.defaultValue !== undefined) {
      onChange(field.defaultValue);
    }
  });

  const helpText = (() => {
    return field.helpText ? (
      <ParagraphXSmall
        marginTop={theme.sizing.scale0}
        marginBottom={theme.sizing.scale300}
        color={theme.colors.contentTertiary}
      >
        {field.helpText}
      </ParagraphXSmall>
    ) : null;
  })();

  return (
    <FlexColumn>
      <FlexColumn gap={theme.sizing.scale200}>
        {fieldConfig.renderType === 'input' && (
          <FormControl
            label={fieldLabel}
            caption={field.helpText}
            overrides={{
              LabelContainer: {
                style: {
                  marginTop: 0,
                  marginBottom: 0,
                },
              },
              ControlContainer: {
                style: { marginBottom: 0 },
              },
            }}
          >
            <Input
              name={field.name}
              placeholder={field.name}
              required={field.required}
              size="compact"
              type={fieldConfig.inputType}
              value={field.value ?? field.defaultValue}
              maxLength={field.maxLength}
              onChange={(e) => onChange(e.currentTarget.value)}
            />
          </FormControl>
        )}
        {fieldConfig.renderType === 'select' && (
          <>
            <EnhancedLabeledSelect
              label={field.name}
              placeholder="Selecciona..."
              required={field.required}
              value={
                field.value || field.defaultValue
                  ? [
                      {
                        id: field.value || field.defaultValue,
                        label:
                          field.options?.find((option) => option.value === (field.value || field.defaultValue))?.text ||
                          field.defaultValue,
                      },
                    ]
                  : []
              }
              options={field.options?.map((option) => ({ id: option.value, label: option.text }))}
              onChange={(params: OnChangeParams) => {
                const value = params.option?.id;
                onChange(value as string);
              }}
            />
            {helpText}
          </>
        )}
      </FlexColumn>
    </FlexColumn>
  );
}
